import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Bot, Globe, CheckCircle2, XCircle, Loader2, ChevronRight, Lightbulb, Clock, Trash2, Wrench, Sparkles, ArrowUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const geoGradeConfig = {
  A: { bg: 'bg-[#00E676]', text: 'text-black' },
  B: { bg: 'bg-[#8BC34A]', text: 'text-black' },
  C: { bg: 'bg-orange-500', text: 'text-white' },
  D: { bg: 'bg-red-500', text: 'text-white' },
};

export default function GeoAuditPage() {
  const { currentSite } = useSite();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [geoAudit, setGeoAudit] = useState(null);
  const [geoAudits, setGeoAudits] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [geoFixResult, setGeoFixResult] = useState(null);

  useEffect(() => {
    if (currentSite?.site_url) {
      setUrl(currentSite.site_url);
    }
  }, [currentSite]);

  useEffect(() => {
    fetchGeoAudits();
  }, [currentSite]);

  const fetchGeoAudits = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = currentSite?.id ? `?site_id=${currentSite.id}` : '';
      const response = await axios.get(`${API_URL}/api/seo/geo-audits${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeoAudits(response.data);
    } catch (error) {
      console.error('Error fetching geo audits:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const deleteGeoAudit = async (e, auditId) => {
    e.stopPropagation();
    if (!window.confirm('Ștergi acest audit GEO?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/seo/geo-audits/${auditId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Audit GEO șters');
      fetchGeoAudits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  const runGeoAutoFix = async () => {
    if (!currentSite?.id) return;
    setFixing(true);
    toast.info('Corectez problemele GEO... Poate dura 30-60 secunde.');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/seo/geo-auto-fix`,
        { site_id: currentSite.id, url: geoAudit.url },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 }
      );
      setGeoFixResult(response.data);
      toast.success('Corectare GEO finalizată!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la corectare GEO');
    } finally {
      setFixing(false);
    }
  };

  const runGeoAudit = async () => {
    if (!url.trim()) {
      toast.error('Introdu un URL valid');
      return;
    }
    setLoading(true);
    setGeoAudit(null);
    setGeoFixResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/seo/geo-audit`,
        { url: url.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeoAudit(response.data);
      toast.success('Audit GEO finalizat!');
      fetchGeoAudits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la audit GEO');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
          <Bot className="w-8 h-8 text-primary" />
          Audit GEO — AI Readiness
        </h1>
        <p className="text-muted-foreground mt-1">
          Verifică cât de pregătit e site-ul pentru motoarele AI (ChatGPT, Perplexity, Claude)
          {currentSite && <span className="text-primary ml-1">• {currentSite.site_name || currentSite.site_url}</span>}
        </p>
      </div>

      {/* URL Input */}
      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="https://exemplu.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="bg-secondary border-border"
              />
            </div>
            <Button
              onClick={runGeoAudit}
              disabled={loading}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analizez...</>
              ) : (
                <><Bot className="w-4 h-4 mr-2" />Rulează Audit GEO</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Eroare */}
      {geoAudit && !geoAudit.success && (
        <Card className="bg-card border-red-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-500">
              <XCircle className="w-6 h-6" />
              <span>{geoAudit.error || 'Nu s-a putut realiza auditul GEO'}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rezultate */}
      {geoAudit && geoAudit.success && (
        <>
          {/* Scor + Grad */}
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    {geoAudit.url}
                  </CardTitle>
                  <CardDescription>
                    Analizat la {formatDate(geoAudit.analyzed_at)}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className={`w-16 h-16 rounded-full ${geoGradeConfig[geoAudit.geo_grade]?.bg} ${geoGradeConfig[geoAudit.geo_grade]?.text} flex items-center justify-center`}>
                      <span className="text-2xl font-bold">{geoAudit.geo_grade}</span>
                    </div>
                    <span className="text-sm text-muted-foreground mt-1">GEO Grade</span>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">{geoAudit.geo_score}</div>
                    <span className="text-sm text-muted-foreground">/100</span>
                  </div>
                  <Button
                    onClick={runGeoAutoFix}
                    disabled={fixing || !currentSite?.id}
                    title={!currentSite?.id ? 'Conectează un site WP mai întâi' : undefined}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {fixing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Corectez...</>
                    ) : (
                      <><Wrench className="w-4 h-4 mr-2" />Corectează Automat</>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* 4 carduri categorii */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {geoAudit.categories?.map((cat) => (
              <Card key={cat.name} className="bg-card border-border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{cat.name}</CardTitle>
                    <Badge variant="outline">{cat.score}/{cat.max}</Badge>
                  </div>
                  <Progress value={(cat.score / cat.max) * 100} className="h-2 mt-2" />
                </CardHeader>
                <CardContent className="space-y-2">
                  {cat.checks.map((check, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      {check.passed ? (
                        <CheckCircle2 className="w-4 h-4 text-[#00E676] flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <span className={check.passed ? 'text-foreground' : 'text-muted-foreground'}>
                          {check.label}
                        </span>
                        {check.detail && (
                          <p className="text-xs text-muted-foreground mt-0.5">{check.detail}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recomandări */}
          {geoAudit.recommendations?.length > 0 && (
            <Card className="bg-card border-[#00E676]/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#00E676]">
                  <Lightbulb className="w-5 h-5" />
                  Recomandări GEO ({geoAudit.recommendations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {geoAudit.recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-[#00E676]/5 rounded-lg border border-[#00E676]/20">
                    <ChevronRight className="w-4 h-4 text-[#00E676] flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{rec}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Rezultate Corectare GEO */}
          {geoFixResult && (
            <Card className="bg-card border-green-500/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#00E676]">
                  <Sparkles className="w-5 h-5" />
                  Rezultate Corectare GEO Automată
                </CardTitle>
                <CardDescription>
                  Scor original: {geoFixResult.original_geo_score}/100 → Estimat: {geoFixResult.estimated_new_score}/100
                  {geoFixResult.estimated_new_score > geoFixResult.original_geo_score && (
                    <span className="ml-2 text-[#00E676]">(+{geoFixResult.estimated_new_score - geoFixResult.original_geo_score} puncte)</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {geoFixResult.fixes_applied?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-[#00E676] mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Acțiuni efectuate ({geoFixResult.fixes_applied.length})
                    </h4>
                    <div className="space-y-2">
                      {geoFixResult.fixes_applied.map((fix, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 bg-[#00E676]/10 rounded">
                          <CheckCircle2 className="w-4 h-4 text-[#00E676] mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{fix}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {geoFixResult.suggestions?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      De adăugat manual ({geoFixResult.suggestions.length})
                    </h4>
                    <div className="space-y-3">
                      {geoFixResult.suggestions.map((s, idx) => (
                        <div key={idx} className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                          <Badge variant="outline" className="text-blue-400 border-blue-500/30 mb-2">{s.label}</Badge>
                          {s.code && (
                            <pre className="text-xs bg-secondary/80 p-2 rounded overflow-x-auto mb-2 text-green-300 whitespace-pre-wrap">{s.code}</pre>
                          )}
                          {s.suggested && (
                            <p className="text-sm text-[#00E676] mb-2"><strong>Text:</strong> {s.suggested}</p>
                          )}
                          {s.suggested_content && (
                            <pre className="text-xs bg-secondary/80 p-2 rounded overflow-x-auto mb-2 text-green-300 whitespace-pre-wrap">{s.suggested_content}</pre>
                          )}
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <ArrowUp className="w-3 h-3 flex-shrink-0" />{s.action}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {geoFixResult.fixes_failed?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-500 mb-2 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Eșuate ({geoFixResult.fixes_failed.length})
                    </h4>
                    <div className="space-y-2">
                      {geoFixResult.fixes_failed.map((fail, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 bg-red-500/10 rounded">
                          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-red-400">{fail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
      {/* Istoric Audituri GEO */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#00E676]" />
            Istoric Audituri GEO
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#00E676]" />
            </div>
          ) : geoAudits.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Niciun audit GEO încă. Rulează primul tău audit!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {geoAudits.slice(0, 10).map((audit) => (
                <div
                  key={audit.id}
                  className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg hover:bg-secondary/70 cursor-pointer transition-colors"
                  onClick={() => setGeoAudit(audit)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${geoGradeConfig[audit.geo_grade]?.bg || 'bg-gray-500'} ${geoGradeConfig[audit.geo_grade]?.text || 'text-white'} flex items-center justify-center`}>
                      <span className="text-sm font-bold">{audit.geo_grade}</span>
                    </div>
                    <div>
                      <p className="font-medium truncate max-w-[300px]">{audit.url}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(audit.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-bold">{audit.geo_score}/100</div>
                      <div className="text-xs text-muted-foreground">GEO Score</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    <button
                      onClick={(e) => deleteGeoAudit(e, audit.id)}
                      className="p-1 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
